import React from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle } from 'lucide-react'
import OrderConfirmationHeader from './OrderConfirmationHeader'
import OrderConfirmationContent, { OrderConfirmationSummary } from './OrderConfirmationContent'
import OrderConfirmationActions from './OrderConfirmationActions'
import Receipt from '../receipt/Receipt'
import checkCircleIcon from '../../assets/images/check_circle.svg'

interface OrderConfirmationLayoutProps {
  order: any
  onNewOrder: () => void
  onPrint: () => void
  isRTL: boolean
  showPrintButton: boolean
  isCustomerService?: boolean
}

const OrderConfirmationLayout: React.FC<OrderConfirmationLayoutProps> = ({
  order,
  onNewOrder,
  onPrint,
  isRTL,
  showPrintButton,
  isCustomerService = false
}) => {
  const { t } = useTranslation()
  return (
    <div className="w-full h-full bg-[#f5f5f5] flex flex-col justify-start items-start" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="w-full h-full flex flex-col justify-center items-center gap-3 p-2">
        {/* Success Icon and Message */}
        <div className="flex flex-col justify-center items-center gap-2">
          <img src={checkCircleIcon} alt="" />
          <div className="text-center text-text-primary text-lg font-medium font-['IBM_Plex_Sans_Arabic']">
            {t('order.success_message')}
          </div>
        </div>

        {/* Order Details Card */}
        <div className="h-[calc(100vh-250px)] min-h-[400px] max-h-[590px] w-full max-w-[480px] bg-white rounded-tl-[25px] rounded-tr-[25px] flex flex-col justify-start items-center overflow-hidden">
          {/* Order Number Box */}
          {order?.id && (
            <div className="w-full flex justify-center items-center -mt-[12px] mb-2">
              <div className="h-[35px] w-[80px] bg-white rounded-bl-[10px] rounded-br-[10px] shadow-md flex items-centers justify-center pt-3">
                <span className="text-sm font-medium font-['IBM_Plex_Sans_Arabic'] text-black">
                  #{order.id}
                </span>
              </div>
            </div>
          )}

          {/* Header - Fixed */}
          <div className="w-full px-4 pt-0 pb-2 flex-shrink-0">
            <OrderConfirmationHeader order={order} isRTL={isRTL} />
          </div>

          {/* Scrollable Items Section */}
          <div className="w-full px-4 flex-1 overflow-y-auto min-h-0">
            <OrderConfirmationContent order={order} isRTL={isRTL} />
          </div>

          {/* Summary - Fixed at Bottom */}
          <div className="w-full px-4 pb-2 flex-shrink-0 bg-white">
            <OrderConfirmationSummary order={order} isRTL={isRTL} />
          </div>

          {/* Receipt Pattern */}
          <div className="w-full px-2 pt-3 bg-white flex justify-between items-center flex-shrink-0">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="w-8 h-4 bg-neutral-100 rounded-sm" />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <OrderConfirmationActions
          onNewOrder={onNewOrder}
          onPrint={onPrint}
          isRTL={isRTL}
          showPrintButton={showPrintButton}
        />
      </div>

      {/* Receipt Component - Only visible when printing (hidden for CS users) */}
      {order && !isCustomerService && (
        <Receipt order={order} isRTL={isRTL} />
      )}
    </div>
  )
}

export default OrderConfirmationLayout

